import React from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { MultiSelect, MultiSelectOption } from '../../components/shared/MultiSelect';
import { ScrollArea } from '../../components/ui/scroll-area';
import { InventoryItemListFilters } from '../../services/consumableInventoryService';

// Filter fields the drawer edits — everything in InventoryItemListFilters
// except the ones the listing page already owns directly (free-text search,
// stock-status tab, sort, pagination).
export type ItemAdvancedFilterState = Pick<
    InventoryItemListFilters,
    'categoryId' | 'vendor' | 'isRefundable' | 'status' | 'minStockLevelMin' | 'minStockLevelMax' | 'totalStockMin' | 'totalStockMax'
>;

export const EMPTY_ITEM_FILTERS: ItemAdvancedFilterState = {};

export function countActiveItemFilters(filters: ItemAdvancedFilterState): number {
    return Object.values(filters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ''
    ).length;
}

const REFUNDABLE_OPTIONS: MultiSelectOption[] = [
    { value: 'true', label: 'Refundable' },
    { value: 'false', label: 'Non-Refundable' },
];
const STATUS_OPTIONS: MultiSelectOption[] = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
];

interface InventoryItemAdvancedFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** In-progress edits, owned by the page's table-state hook. */
    draft: ItemAdvancedFilterState;
    onDraftChange: (filters: ItemAdvancedFilterState) => void;
    onApply: () => void;
    onClear: () => void;
    categoryOptions: MultiSelectOption[];
    vendorOptions: MultiSelectOption[];
}

export const InventoryItemAdvancedFilters: React.FC<InventoryItemAdvancedFiltersProps> = ({
    open,
    onOpenChange,
    draft,
    onDraftChange,
    onApply,
    categoryOptions,
    vendorOptions,
    onClear,
}) => {
    // The draft lives in the page's table-state hook, which resets it to the
    // applied filters whenever the drawer opens — so editing here never
    // mutates the list until "Apply Filters" commits it.
    const set = <K extends keyof ItemAdvancedFilterState>(key: K, value: ItemAdvancedFilterState[K]) => {
        onDraftChange({ ...draft, [key]: value });
    };

    const handleApply = () => {
        onApply();
        onOpenChange(false);
    };

    // onClear resets both the applied filters and the draft in one go.
    const handleClearAll = () => {
        onClear();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-md">
                <SheetHeader>
                    <SheetTitle>Advanced Filters</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Classification</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="ifilter-category">Category</Label>
                                <MultiSelect
                                    id="ifilter-category"
                                    options={categoryOptions}
                                    selected={(draft.categoryId ?? []).map(String)}
                                    onChange={(v) => set('categoryId', v.length ? v.map(Number) : undefined)}
                                    placeholder="Any category"
                                    searchPlaceholder="Search categories…"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="ifilter-vendor">Vendor</Label>
                                <MultiSelect
                                    id="ifilter-vendor"
                                    options={vendorOptions}
                                    selected={draft.vendor ?? []}
                                    onChange={(v) => set('vendor', v.length ? v : undefined)}
                                    placeholder="Any vendor"
                                    searchPlaceholder="Search vendors…"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Item Attributes</h4>
                            <div className="space-y-1.5">
                                <Label htmlFor="ifilter-refundable">Refundable</Label>
                                <MultiSelect
                                    id="ifilter-refundable"
                                    options={REFUNDABLE_OPTIONS}
                                    selected={draft.isRefundable !== undefined ? [draft.isRefundable] : []}
                                    onChange={(v) => set('isRefundable', (v[v.length - 1] as 'true' | 'false' | undefined))}
                                    placeholder="Any"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="ifilter-status">Item Status</Label>
                                <MultiSelect
                                    id="ifilter-status"
                                    options={STATUS_OPTIONS}
                                    selected={draft.status !== undefined ? [draft.status] : []}
                                    onChange={(v) => set('status', (v[v.length - 1] as 'active' | 'inactive' | undefined))}
                                    placeholder="Any"
                                />
                            </div>
                        </div>

                        <div className="space-y-4 border-t pt-5">
                            <h4 className="text-sm font-semibold">Stock Levels</h4>
                            <div className="space-y-1.5">
                                <Label>Minimum Stock Level Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Minimum stock level, lower bound"
                                        placeholder="Min"
                                        value={draft.minStockLevelMin ?? ''}
                                        onChange={(e) => set('minStockLevelMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Minimum stock level, upper bound"
                                        placeholder="Max"
                                        value={draft.minStockLevelMax ?? ''}
                                        onChange={(e) => set('minStockLevelMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Total Stock Range</Label>
                                <div className="grid grid-cols-2 gap-3">
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Total stock minimum"
                                        placeholder="Min"
                                        value={draft.totalStockMin ?? ''}
                                        onChange={(e) => set('totalStockMin', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                    <Input
                                        type="number"
                                        min={0}
                                        aria-label="Total stock maximum"
                                        placeholder="Max"
                                        value={draft.totalStockMax ?? ''}
                                        onChange={(e) => set('totalStockMax', e.target.value === '' ? undefined : Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button type="button" variant="outline" onClick={handleClearAll}>
                        Clear All
                    </Button>
                    <Button type="button" onClick={handleApply}>
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
